import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Tag,
  Checkbox,
  Space,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  StarOutlined,
  StarFilled,
  RedoOutlined,
} from "@ant-design/icons";
import { adminApi, BankAccount, CreateBankAccountData } from "@app/api/admin";
import { ColumnsType } from "antd/lib/table";
import { response } from "express";
import { useUpdateEffect } from "@app/hooks/useUpdateEffect";

interface BankAccountManagementProps {
  employeeId?: number;
  employeeName?: string;
}

const BankAccountManagement: React.FC<BankAccountManagementProps> = ({
  employeeId,
  employeeName,
}) => {
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BankAccount | null>(
    null
  );
  const [form] = Form.useForm();

  // 获取银行账户列表
  const fetchBankAccounts = async () => {
    try {
      setLoading(true);
      const { data } = await adminApi.bankAccounts.getByEmployeeId(employeeId!);
      setBankAccounts(data);
    } catch (error) {
      message.error("获取银行账户失败");
    } finally {
      setLoading(false);
    }
  };

  useUpdateEffect(() => {
    if (employeeId) {
      fetchBankAccounts();
    }
  }, [employeeId]);

  // 新增/编辑银行账户
  const handleSubmit = async (values: any) => {
    try {
      const data: CreateBankAccountData = {
        employee_id: employeeId,
        ...values,
      };

      if (editingAccount) {
        await adminApi.bankAccounts.update(editingAccount.id, data);
        message.success("更新银行账户成功");
      } else {
        await adminApi.bankAccounts.create(data);
        message.success("添加银行账户成功");
      }

      setModalVisible(false);
      form.resetFields();
      setEditingAccount(null);
      fetchBankAccounts();
    } catch (error) {
      message.error(editingAccount ? "更新银行账户失败" : "添加银行账户失败");
    }
  };

  // 删除银行账户
  const handleDelete = async (id: number) => {
    try {
      await adminApi.bankAccounts.delete(id);
      message.success("删除银行账户成功");
      fetchBankAccounts();
    } catch (error) {
      message.error("删除银行账户失败");
    }
  };

  // 设置默认账户
  const handleSetDefault = async (accountId: number) => {
    try {
      await adminApi.bankAccounts.setDefault(employeeId!, {
        account_id: accountId,
      });
      message.success("设置默认账户成功");
      fetchBankAccounts();
    } catch (error) {
      console.error("设置默认账户失败");
    }
  };

  // 打开编辑模态框
  const handleEdit = (account: BankAccount) => {
    setEditingAccount(account);
    form.setFieldsValue({
      id: account.id,
      bank_name: account.bank_name,
      card_number: account.card_number,
      account_name: account.account_name,
      is_default: account.is_default,
    });
    setModalVisible(true);
  };

  // 打开新增模态框
  const handleAdd = () => {
    setEditingAccount(null);
    form.resetFields();
    form.setFieldsValue({
      account_holder_name: employeeName,
      status: 1,
      is_default: false,
    });
    setModalVisible(true);
  };

  const [columns] = useState<ColumnsType<BankAccount>>([
    {
      title: "银行名称",
      dataIndex: "bank_name",
      key: "bank_name",
    },
    {
      title: "银行卡号码",
      dataIndex: "card_number",
      key: "card_number",
      render: (text: string) => {
        // 隐藏中间部分账号
        if (text && text.length > 8) {
          return `${text.slice(0, 4)}****${text.slice(-4)}`;
        }
        return text;
      },
    },
    {
      title: "账户持有人",
      dataIndex: "account_name",
      key: "account_name",
    },
    {
      title: "默认",
      dataIndex: "is_default",
      key: "is_default",
      render: (is_default: boolean) => (
        <Tag color={is_default ? "gold" : "blue"}>
          {is_default ? "默认" : "非默认"}
        </Tag>
      ),
    },

    {
      title: "操作",
      key: "action",
      render: (_, record: BankAccount) => (
        <>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          {!record.is_default && (
            <Button
              type="link"
              size="small"
              icon={<StarOutlined />}
              onClick={() => handleSetDefault(record.id)}
            >
              设为默认
            </Button>
          )}
          <Popconfirm
            title="确定要删除这个银行账户吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ]);

  return (
    <Card
      title={`${employeeName}的银行账户管理`}
      extra={
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            添加银行账户
          </Button>
          <Button
            type="primary"
            icon={<RedoOutlined />}
            loading={loading}
            onClick={fetchBankAccounts}
          >
            刷新
          </Button>
        </Space>
      }
    >
      <Table
        bordered
        columns={columns}
        dataSource={bankAccounts}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
      />

      <Modal
        title={editingAccount ? "编辑银行账户" : "添加银行账户"}
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingAccount(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="bank_name"
            label="银行名称"
            rules={[{ required: true, message: "请输入银行名称" }]}
          >
            <Input placeholder="请输入银行名称" />
          </Form.Item>

          <Form.Item
            name="card_number"
            label="银行卡号码"
            rules={[{ required: true, message: "请输入银行卡号码" }]}
          >
            <Input placeholder="请输入银行卡号码" />
          </Form.Item>

          <Form.Item
            name="account_name"
            label="账户持有人"
            rules={[{ required: true, message: "请输入账户持有人姓名" }]}
          >
            <Input placeholder="请输入账户持有人姓名" />
          </Form.Item>

        

          <Form.Item>
            <div style={{ textAlign: "right" }}>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setEditingAccount(null);
                }}
                style={{ marginRight: 8 }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingAccount ? "更新" : "添加"}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default BankAccountManagement;
